// กรอกคำตอบทั้งห้อง — หนึ่งแถวต่อนักเรียน คำตอบปรนัยพิมพ์ต่อกันเป็นสตริงเดียว ("13422-4ถผ")
// ตัวอักษรละหนึ่งข้อ ไล่ตามข้อปรนัย/ใช่-ไม่ใช่ (ข้ามข้ออัตนัยซึ่งมีช่องคะแนนแยก)
//
// ปุ่มตัวเลขอ่านจาก e.code ไม่ใช่ e.key เพราะเครื่องที่เปิดแป้นไทยค้างไว้ กด 1 2 3 4 แล้วได้ "ๅ/-ภ"
// ซึ่ง "-" ในนั้นแปลว่าไม่ตอบ — ครูจะกรอกผิดทั้งห้องโดยไม่รู้ตัว
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Badge, Button, Form, Table } from 'react-bootstrap'
import Swal from 'sweetalert2'
import axiosInstance from '../services/axiosInterceptor'
import { useLoading } from '../contexts/LoadingContext'
import { API, answerLabel, fmt, isObjective, labelSet, showError } from './examUtils'

const toText = (answers, objective, labels) => {
  if (!answers) return ''
  const chars = objective.map((q) => answerLabel(answers[q.eq_no]?.ea_answer, labels) || '-')
  return chars.join('').replace(/-+$/, '')
}

const toWritten = (answers, written) => Object.fromEntries(
  written.map((q) => {
    const s = answers?.[q.eq_no]?.ea_score
    return [q.eq_no, s === null || s === undefined ? '' : String(s)]
  })
)

const inactive = (s) => String(s.st_status) === '0' || s.transfer_room_id != null

export default function ExamEntry({ examId, exam, questions, room, students, onSaved }) {
  const { setLoading } = useLoading()
  const objective = useMemo(() => questions.filter(isObjective), [questions])
  const written = useMemo(() => questions.filter((q) => q.eq_type === 'written'), [questions])
  const inputs = useRef({})
  const labels = labelSet(exam)

  const original = useMemo(() => Object.fromEntries(students.map((s) => [s.study_id, {
    text: toText(s.answers, objective, labels),
    written: toWritten(s.answers, written),
  }])), [students, objective, written, labels])

  const [draft, setDraft] = useState(original)
  // โหลดใหม่หลังบันทึก = ค่าที่บันทึกแล้วกลายเป็นค่าตั้งต้นชุดใหม่ (server คืนมาเป็น ก ข ค แทนเลขที่พิมพ์)
  useEffect(() => setDraft(original), [original])
  const [hideInactive, setHideInactive] = useState(true)

  const isDirty = (id) => {
    const a = draft[id]
    const b = original[id]
    if (!a || !b) return false
    return a.text !== b.text || written.some((q) => (a.written[q.eq_no] ?? '') !== (b.written[q.eq_no] ?? ''))
  }
  const dirtyIds = students.map((s) => s.study_id).filter(isDirty)
  const visible = students.filter((s) => !hideInactive || !inactive(s) || s.answers)

  const setText = (id, text) => setDraft((d) => ({ ...d, [id]: { ...d[id], text } }))
  const setWritten = (id, no, value) => setDraft((d) => ({ ...d, [id]: { ...d[id], written: { ...d[id].written, [no]: value } } }))

  const focusNext = (index) => {
    const next = visible[index + 1]
    if (next) inputs.current[next.study_id]?.focus()
  }

  // แทรกตัวอักษรที่ตำแหน่งเคอร์เซอร์เอง เมื่อปุ่มที่กดเป็นปุ่มตัวเลข/ขีด/เว้นวรรค
  const onKeyDown = (e, s, index) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      focusNext(index)
      return
    }
    if (e.ctrlKey || e.metaKey || e.altKey) return
    const el = e.target
    const pos = el.selectionStart ?? el.value.length
    const q = objective[pos]
    let ch = null
    const digit = /^(Digit|Numpad)([0-5])$/.exec(e.code || '')
    if (digit && e.shiftKey) {
      // Shift+3 = "#" (ฝนหลายช่อง) — แป้นไทยให้ตัวอื่นที่ตำแหน่งนี้ จึงแปลงเองเหมือนกัน
      ch = digit[2] === '3' ? '#' : null
    } else if (digit) {
      // ใช่/ไม่ใช่: แป้นไทยพิมพ์ "ถ" ที่ปุ่มเลข 5 — ปล่อยให้เป็น ถ ในข้อที่เป็นใช่/ไม่ใช่
      ch = q?.eq_type === 'truefalse' && e.key === 'ถ' ? 'ถ' : digit[2] === '0' ? '-' : digit[2]
    } else if (e.code === 'Minus' || e.code === 'NumpadSubtract' || e.code === 'Space') {
      ch = '-'
    }
    if (!ch) return
    e.preventDefault()
    if (el.value.length >= objective.length && el.selectionStart === el.selectionEnd) return
    const start = el.selectionStart ?? el.value.length
    const end = el.selectionEnd ?? start
    const next = (el.value.slice(0, start) + ch + el.value.slice(end)).slice(0, objective.length)
    setText(s.study_id, next)
    requestAnimationFrame(() => {
      el.setSelectionRange(start + 1, start + 1)
      if (next.length >= objective.length && start + 1 >= objective.length && !written.length) focusNext(index)
    })
  }

  const save = async () => {
    if (!dirtyIds.length) return
    const responses = dirtyIds.map((id) => {
      const d = draft[id]
      const chars = [...d.text]
      return {
        study_id: id,
        answers: Object.fromEntries(objective.map((q, i) => [q.eq_no, chars[i] ?? ''])),
        written: d.written,
      }
    })
    setLoading(true)
    try {
      const res = await axiosInstance.put(`${API}/exams/${examId}/rooms/${room.teach_id}/responses`, { responses })
      Swal.fire({
        icon: 'success',
        title: 'บันทึกแล้ว',
        text: `บันทึก ${res.data.saved} คน${res.data.cleared ? ` · ล้างคำตอบ ${res.data.cleared} คน` : ''}`,
        timer: 1500,
        showConfirmButton: false,
      })
      await onSaved()
    } catch (err) {
      showError(err, 'บันทึกไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }

  // คำตอบที่บันทึกแล้ว ระบายสีถูก/ผิดตามเฉลยปัจจุบัน ให้ครูเห็นว่ากรอกเลื่อนข้อไปหรือเปล่า
  const Checked = ({ s }) => {
    if (!s.score) return null
    return (
      <div className="font-monospace fs-xs text-nowrap" style={{ letterSpacing: 1 }}>
        {objective.map((q) => {
          const a = s.answers?.[q.eq_no]?.ea_answer
          const got = s.score.perQuestion[q.eq_no]
          const cls = got === null ? 'text-secondary' : got > 0 ? 'text-success' : 'text-danger'
          return <span key={q.eq_no} className={cls}>{answerLabel(a, labels) || '·'}</span>
        })}
      </div>
    )
  }

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <div className="fs-sm text-secondary me-auto">
          พิมพ์คำตอบต่อกันทีละคน: <b>1–{exam.exam_choices}</b> หรือ <b>{labels.choice[0]}–{labels.choice[exam.exam_choices - 1]}</b>,
          ใช่/ไม่ใช่ใช้ <b>1/2</b> หรือ <b>{labels.tf.join('/')}</b>, ไม่ตอบใช้ <b>-</b> หรือ <b>0</b>, ฝนหลายช่องใช้ <b>#</b> ·
          กด Enter ไปคนถัดไป
        </div>
        <Form.Check type="switch" id="hide-inactive" label="ซ่อนคนที่ย้าย/ออกแล้ว"
          checked={hideInactive} onChange={(e) => setHideInactive(e.target.checked)} />
        <Button size="sm" disabled={!dirtyIds.length} onClick={save}>
          <i className="ri-save-line"></i> บันทึก{dirtyIds.length ? ` (${dirtyIds.length} คน)` : ''}
        </Button>
      </div>

      {objective.some((q) => !q.eq_key) && (
        <Alert variant="warning" className="fs-sm py-2">
          ยังมีข้อที่ไม่ได้ใส่เฉลย — กรอกคำตอบได้ตามปกติ แต่ข้อนั้นจะยังไม่ถูกคิดคะแนน
        </Alert>
      )}

      <div className="table-responsive">
        <Table size="sm" hover className="align-middle mb-0">
          <thead>
            <tr>
              <th style={{ width: 45 }}>เลขที่</th>
              <th>ชื่อ-สกุล</th>
              {objective.length > 0 && <th style={{ minWidth: 260 }}>คำตอบข้อ {objective[0].eq_no}–{objective[objective.length - 1].eq_no}</th>}
              {written.map((q) => <th key={q.eq_no} style={{ width: 80 }}>ข้อ {q.eq_no}<div className="fs-xs fw-normal">เต็ม {fmt(q.eq_points)}</div></th>)}
              <th style={{ width: 80 }} className="text-end">คะแนน</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((s, index) => {
              const d = draft[s.study_id] || { text: '', written: {} }
              const dirty = isDirty(s.study_id)
              return (
                <tr key={s.study_id} className={dirty ? 'table-warning' : undefined}>
                  <td>{s.seat}</td>
                  <td>
                    <div className="text-nowrap">{s.st_name}</div>
                    <div className="fs-xs text-secondary">
                      {s.st_idnumber}
                      {inactive(s) && <Badge bg="secondary" className="ms-1">{String(s.st_status) === '0' ? 'ออกแล้ว' : 'ย้ายห้อง'}</Badge>}
                    </div>
                  </td>
                  {objective.length > 0 && (
                    <td>
                      <Form.Control
                        size="sm"
                        ref={(el) => { inputs.current[s.study_id] = el }}
                        className="font-monospace"
                        style={{ letterSpacing: 2 }}
                        value={d.text}
                        maxLength={objective.length}
                        autoComplete="off"
                        spellCheck={false}
                        inputMode="numeric"
                        onKeyDown={(e) => onKeyDown(e, s, index)}
                        onChange={(e) => setText(s.study_id, e.target.value.slice(0, objective.length))}
                      />
                      <div className="d-flex justify-content-between gap-2">
                        {dirty ? <span className="fs-xs text-warning">ยังไม่ได้บันทึก</span> : <Checked s={s} />}
                        <span className="fs-xs text-secondary text-nowrap">{d.text.length}/{objective.length}</span>
                      </div>
                    </td>
                  )}
                  {written.map((q) => (
                    <td key={q.eq_no}>
                      <Form.Control size="sm" inputMode="decimal" value={d.written[q.eq_no] ?? ''}
                        onChange={(e) => setWritten(s.study_id, q.eq_no, e.target.value.replace(/[^0-9.]/g, ''))} />
                    </td>
                  ))}
                  <td className="text-end">
                    {s.score ? (
                      <>
                        <b>{fmt(s.score.total)}</b>
                        {!s.score.complete && <div className="fs-xs text-warning">ยังไม่ครบ</div>}
                      </>
                    ) : <span className="text-secondary">-</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </div>
      <div className="fs-xs text-secondary mt-2">
        ล้างคำตอบของใครจนว่างทั้งแถวแล้วบันทึก = ถือว่ายังไม่ได้สอบ (ไม่ใช่ได้ศูนย์)
      </div>
    </>
  )
}
